import type { FunnelVariable } from '@segmently/schema-models/v2';
import type { WebPlacementConfig } from '@modules/webplacements/types/webplacements.types';

export type WebPlacementReferenceIssueType =
  | 'missing-variable'
  | 'missing-screen'
  | 'missing-url-param';

export interface WebPlacementReferenceIssue {
  type: WebPlacementReferenceIssueType;
  area: string;
  id: string;
  label?: string;
}

export interface WebPlacementReferenceValidationInput {
  config: WebPlacementConfig;
  variables: FunnelVariable[];
  screens: Array<{ id: string; name?: string }>;
}

function addVariableIssue(
  issues: WebPlacementReferenceIssue[],
  variableIds: Set<string>,
  area: string,
  id: string | undefined,
  label?: string,
): void {
  if (id && !variableIds.has(id)) {
    issues.push({
      type: 'missing-variable',
      area,
      id,
      label,
    });
  }
}

export function validateWebPlacementReferences({
  config,
  variables,
  screens,
}: WebPlacementReferenceValidationInput): WebPlacementReferenceIssue[] {
  const issues: WebPlacementReferenceIssue[] = [];
  const variableIds = new Set(variables.map((variable) => variable.id));
  const screenIds = new Set(screens.map((screen) => screen.id));
  const urlParamKeys = new Set(
    (config.urlParams ?? []).flatMap((param) => [
      param.key,
      param.sourceDetails?.urlParam,
    ].filter((key): key is string => Boolean(key))),
  );

  for (const param of config.variableInputs ?? []) {
    addVariableIssue(
      issues,
      variableIds,
      'Variable Inputs',
      param.sourceDetails?.variableId,
      param.label || param.key,
    );
  }

  for (const binding of config.variableBindings ?? []) {
    addVariableIssue(
      issues,
      variableIds,
      'Variable Bindings',
      binding.variableId,
      binding.variableName || binding.sourceKey,
    );
  }

  addVariableIssue(
    issues,
    variableIds,
    'Stripe Email Settings',
    config.stripe?.emailVariableId,
  );

  for (const rule of config.entryScreenRules ?? []) {
    if (rule.targetScreenId && !screenIds.has(rule.targetScreenId)) {
      issues.push({
        type: 'missing-screen',
        area: 'Entry Screen Rules',
        id: rule.targetScreenId,
        label: rule.targetScreenName || rule.paramKey,
      });
    }

    if (rule.paramKey && !urlParamKeys.has(rule.paramKey)) {
      issues.push({
        type: 'missing-url-param',
        area: 'Entry Screen Rules',
        id: rule.paramKey,
        label: rule.targetScreenName,
      });
    }
  }

  return issues;
}

export function formatWebPlacementReferenceIssues(issues: WebPlacementReferenceIssue[]): string {
  return issues.map((issue) => {
    if (issue.type === 'missing-screen') {
      return `${issue.area}: missing screen "${issue.label || issue.id}" (${issue.id})`;
    }

    if (issue.type === 'missing-url-param') {
      return `${issue.area}: missing URL parameter "${issue.id}"`;
    }

    return `${issue.area}: missing variable "${issue.label || issue.id}" (${issue.id})`;
  }).join('; ');
}
